import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowForwardRounded,
  DirectionsCarOutlined,
  EventSeatOutlined,
  RefreshRounded,
  PersonOutlineOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import moment from "moment-timezone";

import DashboardClientLayout from "../../Layouts/DashboardClientLayout";
import { setAuthentication } from "../../Redux/Authentication/actions";
import RideApi from "Services/Ride";
import BookingApi from "Services/Booking";
import WalletApi from "Services/Wallet";
import loginApi from "../../Services/Login";
import passengerApi from "Services/Passenger";
import PlaceService from "../../Services/Place";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import "../TrajetClient/HomePage.css";
import "../Home/HomePage.css";
import "../HomeRideDetails/HomePage.css";
import "./Trajet.css";

const recentFallback = [
  { from: "Yaoundé", to: "Douala", date: "Aujourd'hui" },
  { from: "Douala", to: "Bafoussam", date: "Hier" },
  { from: "Lomé", to: "Cotonou", date: "9 juin" },
];

const popularTrips = [
  { from: "Yaoundé", to: "Douala", price: "à partir de 2 000 XAF", duration: "~ 2h 30m" },
  { from: "Douala", to: "Bafoussam", price: "à partir de 2 500 XAF", duration: "~ 4h 15m" },
  { from: "Yaoundé", to: "Bafoussam", price: "à partir de 3 000 XAF", duration: "~ 5h 30m" },
  { from: "Lomé", to: "Cotonou", price: "à partir de 1 500 XAF", duration: "~ 1h 30m" },
];

const passengerCategoryOptions = [
  "Jeune (0 à 26 ans)",
  "Adulte (27 à 59 ans)",
  "Sénior (60 ans et plus)",
];

const passengerAgeOptions = Array.from({ length: 99 }, (_, index) => String(index + 1));

const comfortClassOptions = [
  { value: "all", label: "Tous" },
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
  { value: "extra_confort", label: "Extra confort" },
  { value: "xl", label: "XL" },
];

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function CirclePinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="2.6" />
      <line x1="8" y1="2.5" x2="8" y2="6" />
      <line x1="16" y1="2.5" x2="16" y2="6" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
    </svg>
  );
}

function ChevronDownIcon(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function formatFrenchDate(date, mode = "long") {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: mode === "short" ? "short" : "long",
  }).format(date);
}

function buildMonthGrid(date) {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const startIndex = (firstDay.getDay() + 6) % 7;
  const totalDays = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < startIndex; index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(new Date(date.getFullYear(), date.getMonth(), day));
  }

  return cells;
}

function formatPlaceResult(place) {
  const primary = place?.name || place?.addressName || place?.addressLocality || "Lieu";
  const secondaryParts = [
    place?.addressLocality,
    place?.addressRegion,
    place?.addressCountry,
  ].filter(Boolean);

  return {
    id: place?.id || place?._id || primary,
    title: primary,
    subtitle: secondaryParts.join(", "),
  };
}

function getAuthPayload(responseData) {
  return responseData?.data || responseData || {};
}

function getAuthToken(authData) {
  return authData?.accessToken || authData?.token || authData?.tokens?.accessToken || authData?.auth?.accessToken;
}

function persistAuthSession(authData) {
  const token = getAuthToken(authData);
  if (!token) return null;

  const user = authData?.user || {};
  localStorage.setItem("isAuthenticated", true);
  localStorage.setItem("token", token);
  localStorage.setItem("tokenExpiration", Date.now() + 24 * 60 * 60 * 1000);
  localStorage.setItem("userid", user?.id || authData?.id || "");
  localStorage.setItem("userType", user?.userType || authData?.userType || "");
  localStorage.setItem("email", user?.email || authData?.email || "");

  if (authData?.sub_companyName) {
    localStorage.setItem("subcompany", authData.sub_companyName);
  } else {
    localStorage.removeItem("subcompany");
  }

  return token;
}

function ReservationLoginModal({ open, onClose, form, setForm, onSubmit, loading, error }) {
  if (!open) return null;

  return (
    <div className="ride-auth-overlay" role="presentation" onClick={onClose}>
      <div
        className="ride-auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ride-auth-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ride-auth-accent" />
        <button type="button" className="ride-auth-close" onClick={onClose} aria-label="Fermer">×</button>
        <div className="ride-auth-header">
          <span className="ride-auth-badge">Connexion requise</span>
          <h2 id="ride-auth-title">Connectez-vous pour réserver</h2>
          <p>La réservation garde l'ambiance Metuaa: simple, propre et rassurante.</p>
        </div>

        <form className="ride-auth-form" onSubmit={onSubmit}>
          <label className="ride-auth-field">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="exemple@email.com"
              autoComplete="email"
            />
          </label>

          <label className="ride-auth-field">
            <span>Mot de passe</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </label>

          {error ? <div className="ride-auth-error">{error}</div> : null}

          <div className="ride-auth-actions">
            <button type="button" className="ride-auth-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="ride-auth-primary" disabled={loading}>
              {loading ? "Connexion..." : "Se connecter et réserver"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PaymentModeModal({ open, onClose, onSelectMode, onConfirm, selectedMode, loading }) {
  if (!open) return null;

  const paymentModes = [
    { key: "cash", label: "CASH" },
    { key: "card", label: "CARD" },
    { key: "wallet", label: "WALLET" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(16, 35, 63, 0.18)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h6" sx={{ fontWeight: 900, color: "#10233f" }}>
          Mode de paiement
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2.5 }}>
        <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap" }}>
          {paymentModes.map((mode) => (
            <Button
              key={mode.key}
              type="button"
              variant="outlined"
              onClick={() => onSelectMode(mode.key)}
              sx={{
                flex: "1 1 96px",
                minWidth: 96,
                height: 44,
                borderRadius: 2,
                borderColor: selectedMode === mode.key ? "#0f6a3b" : "#b9e2c8",
                color: selectedMode === mode.key ? "#fff" : "#0f6a3b",
                fontWeight: 900,
                background: selectedMode === mode.key ? "#0f6a3b" : "#fff",
                textTransform: "uppercase",
                "&:hover": {
                  borderColor: "#0f6a3b",
                  background: "#edf8ec",
                },
              }}
            >
              {mode.label}
            </Button>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="text" sx={{ color: "#64748b", fontWeight: 800 }}>
          Annuler
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={!selectedMode || loading}
          sx={{
            background: "linear-gradient(180deg, #0f6a3b, #0a532e)",
            fontWeight: 900,
            textTransform: "none",
            minWidth: 160,
          }}
        >
          {loading ? "Patientez..." : "Réserver maintenant"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const RecentCard = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      border: "1px solid #e6ebf2",
      background: "#fff",
      px: 2.25,
      py: 1.75,
      boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
      minHeight: 78,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "999px",
          border: "1.5px solid #9aa7b7",
          flexShrink: 0,
          mt: 0.25,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ color: "#3a4b63", fontWeight: 900, lineHeight: 1.2 }}>
          {item.from} <Box component="span" sx={{ color: "#8a96a8", px: 0.5 }}>·</Box> {item.to}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.3 }}>
          {item.date}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const PopularCard = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      border: "1px solid #e6ebf2",
      background: "#fff",
      boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
      px: 2.25,
      py: 2,
      minHeight: 154,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "transform 160ms ease, box-shadow 160ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 16px 28px rgba(8, 26, 52, 0.08)",
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box sx={{ display: "grid", justifyItems: "center", pt: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "999px", border: "1.5px solid #23314a" }} />
          <Box sx={{ width: 2, height: 25, background: "#23314a", my: 0.35, borderRadius: 999 }} />
          <Box sx={{ width: 8, height: 8, borderRadius: "999px", border: "1.5px solid #23314a" }} />
        </Box>

        <Box>
          <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.15 }}>
            {item.from}
          </Typography>
          <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.15, mt: 1 }}>
            {item.to}
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "999px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          background: "rgba(13, 122, 69, 0.1)",
          color: "#0d7a45",
        }}
      >
        <ArrowForwardRounded fontSize="small" />
      </Box>
    </Stack>

    <Stack direction="row" justifyContent="space-between" alignItems="end" sx={{ pt: 2 }}>
      <Box>
        <Typography variant="body2" sx={{ color: "#5d6a7c", fontWeight: 700 }}>
          {item.price}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.5 }}>
          {item.duration}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

function formatTripDateTime(value, timezone) {
  if (!value) {
    return { dateLabel: "--", weekdayLabel: "--", timeLabel: "--:--" };
  }

  const date = moment(value).tz(timezone || "Africa/Douala");
  return {
    dateLabel: date.format("DD MMM YYYY"),
    weekdayLabel: date.format("dddd"),
    timeLabel: date.format("HH:mm"),
  };
}

function formatTripMoney(amount, currency = "FCFA") {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return `-- ${currency}`;
  return `${value.toLocaleString("fr-FR")} ${currency}`;
}

function getTripStopCount(trip) {
  const explicitStops = trip?.stops_count ?? trip?.stop_count ?? trip?.number_of_stops ?? trip?.ride_waypoints?.length;
  const stops = Number(explicitStops);
  if (Number.isFinite(stops) && stops >= 0) return stops;
  return 0;
}

function safeJoin(...parts) {
  return parts.filter(Boolean).join(" ").trim();
}

function formatDurationFromRide(ride, fallback = "--") {
  if (!ride?.departure_at || !ride?.estimated_arrival_at) return fallback;
  const departure = new Date(ride.departure_at);
  const arrival = new Date(ride.estimated_arrival_at);
  if (Number.isNaN(departure.getTime()) || Number.isNaN(arrival.getTime())) return fallback;
  const durationMinutes = Math.max(0, Math.round((arrival.getTime() - departure.getTime()) / 60000));
  const hours = Math.floor(durationMinutes / 60);
  const minutes = String(durationMinutes % 60).padStart(2, "0");
  return `${hours}h${minutes}`;
}

function normalizeTripDetail(trip, timezone) {
  const source = trip?.raw || trip || {};
  const departure = formatTripDateTime(source?.departure_at, timezone);
  const arrival = formatTripDateTime(source?.estimated_arrival_at, timezone);
  const rideWaypoints = Array.isArray(source?.ride_waypoints) ? source.ride_waypoints : [];
  const driverName = safeJoin(
    source?.driver?.first_name,
    source?.driver?.last_name,
    source?.users?.first_name,
    source?.users?.last_name,
  ) || source?.driver_name || "Conducteur";
  const passengerName = safeJoin(
    source?.users?.first_name,
    source?.users?.last_name,
  ) || source?.userFullName || source?.passenger_name || "Passager";
  const vehicleNo =
    source?.vehicle_no ||
    source?.vehicles?.vehicle_no ||
    source?.vehicle?.vehicle_no ||
    source?.vehicleNumber ||
    source?.plate_number ||
    "--";
  const vehicleName =
    source?.vehicle_name ||
    source?.vehicles?.vehicle_name ||
    source?.vehicle?.vehicle_name ||
    source?.vehicleModel ||
    source?.vehicle_model ||
    "--";
  const comfortClass = source?.comfort_class || source?.rides?.comfort_class || source?.ride?.comfort_class || "--";
  const originLabel =
    source?.origin?.addressName ||
    source?.origin?.name ||
    source?.places_rides_origin_place_idToplaces?.name ||
    source?.origin_place_name ||
    source?.origin_place_id ||
      "Départ";
  const destinationLabel =
    source?.destination?.addressName ||
    source?.destination?.name ||
    source?.places_rides_destination_place_idToplaces?.name ||
    source?.destination_place_name ||
    source?.destination_place_id ||
      "Arrivée";
  const stopCount = getTripStopCount(source);
  const waypoints = rideWaypoints
    .map((wp, index) => ({
      id: wp?.id || wp?._id || `${index}-${wp?.sequence_order || index + 1}`,
      name: wp?.places?.name || wp?.place?.name || wp?.place_name || wp?.name || `Arrêt ${index + 1}`,
      sequence: Number(wp?.sequence_order ?? index + 1) || index + 1,
      offsetLabel: Number.isFinite(Number(wp?.arrival_offset_min)) ? `${Number(wp.arrival_offset_min)} min` : "--",
    }))
    .sort((a, b) => a.sequence - b.sequence);

  const bookingStatus = String(source?.status || "pending").toLowerCase();
  const statusLabel = bookingStatus === "confirmed"
    ? "Confirmé"
    : bookingStatus === "cancelled" || bookingStatus === "canceled"
      ? "Annulé"
      : bookingStatus === "completed"
        ? "Terminé"
        : "En attente";
  const statusTone = bookingStatus === "completed" || bookingStatus === "cancelled" || bookingStatus === "canceled"
    ? "is-past"
    : "is-active";

  return {
    raw: source,
    id: source?.id || source?.ride_id || "-",
    rideId: source?.ride_id || source?.id || "-",
    journeyTitle: `${originLabel} → ${destinationLabel}`,
    originLabel,
    destinationLabel,
    departureTime: departure.timeLabel,
    arrivalTime: arrival.timeLabel,
    departureDateLabel: `${departure.weekdayLabel} ${departure.dateLabel}`,
    tripDuration: formatDurationFromRide(source),
    stopCount,
    waypoints,
    driverName,
    passengerName,
    vehicleNo,
    vehicleName,
    comfortClass,
    modeLabel: source?.type === "train" ? "Train" : source?.type === "bus" ? "Bus" : "Covoiturage",
    statusLabel,
    statusTone,
    availableSeats: Number(source?.available_seats ?? 0),
    totalSeats: Number(source?.total_seats ?? 0),
    bookedSeats: Number(source?.seats_booked ?? source?.seatsBooked ?? 0),
    pricePerSeat: Number(source?.price_per_seat ?? 0),
    serviceFee: Number(source?.service_fee ?? 0),
    currency: source?.currency || "FCFA",
    phone: source?.users?.phone || source?.phone || source?.contact_phone || source?.user_phone || "--",
  };
}

const TripRowCard = ({ trip, timezone, onOpenDetails }) => {
  const details = normalizeTripDetail(trip, timezone);

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetails?.(trip)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenDetails?.(trip);
        }
      }}
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 39, 72, 0.08)",
        background: "#fff",
        boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
        px: { xs: 1.75, md: 2.25 },
        py: { xs: 1.6, md: 1.8 },
        cursor: "pointer",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 28px rgba(8, 26, 52, 0.08)",
          borderColor: "rgba(15, 39, 72, 0.14)",
        },
        "&:focus-visible": {
          outline: "2px solid #0d7a45",
          outlineOffset: 2,
        },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "1.05 1 0" },
            display: "grid",
            gap: 1.1,
            pt: 0.5,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 86 }}>
              <Typography variant="body2" sx={{ color: "#0f2a4a", fontWeight: 900, lineHeight: 1.1 }}>
                {details.departureTime}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700 }}>
                {details.departureDateLabel}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.1 }}>
                {details.originLabel}
              </Typography>
              <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.1, mt: 0.75 }}>
                {details.destinationLabel}
              </Typography>
            </Box>
          </Stack>

          
        </Box>
          <Box
          sx={{
            minWidth: 0,
            flex: { xs: "1 1 auto", md: "1.8 1 0" },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "auto auto auto auto" },
            columnGap: { xs: 1.5, md: 1.75 },
            rowGap: { xs: 1.5, md: 0 },
            alignItems: "center",
          }}
        >
          <Stack direction="column" spacing={0.35} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Chip
              label={details.modeLabel}
              size="small"
              sx={{
                borderRadius: "999px",
                background: "#eff8f1",
                color: "#0d7a45",
                fontWeight: 800,
                width: "fit-content",
              }}
            />
            <Chip
              label={details.comfortClass}
              size="small"
              sx={{
                borderRadius: "999px",
                background: "#f4f7fb",
                color: "#334155",
                fontWeight: 800,
                width: "fit-content",
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "11px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                background: "rgba(13, 122, 69, 0.1)",
                color: "#0d7a45",
              }}
            >
              <DirectionsCarOutlined fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05 }}>
                {details.vehicleNo}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.25 }}>
                {details.vehicleName}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: "rgba(13, 122, 69, 0.1)",
                  color: "#0d7a45",
                }}
              >
                <EventSeatOutlined fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05 }}>
                  Sièges disponibles
                </Typography>
                <Typography variant="body2" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.25 }}>
                  {details.availableSeats}/{details.totalSeats || "--"} sieges
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "11px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                background: "rgba(13, 122, 69, 0.1)",
                color: "#0d7a45",
              }}
            >
              <PersonOutlineOutlined fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05, textTransform: "lowercase" }}>
                {details.passengerName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.25 }}>
                {details.phone}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" flexShrink={0}>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6" sx={{ color: "#0d7a45", fontWeight: 900, lineHeight: 1.05 }}>
              {formatTripMoney(details.pricePerSeat, details.currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700 }}>
              per seat
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={() => onOpenDetails?.(trip)}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 900,
              minWidth: 132,
              borderColor: "#b8dbc3",
              color: "#0d7a45",
              background: "#fff",
              "&:hover": {
                borderColor: "#94c9a4",
                background: "#fbfefb",
              },
            }}
          >
            Voir les details
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

function ClockMiniIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 2" />
    </svg>
  );
}

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "grid", gap: 0.35 }}>
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 700, wordBreak: "break-word" }}>
      {value || "--"}
    </Typography>
  </Box>
);

const RideDetailsModal = ({ open, ride, loading, error, onClose, passengerCount, bookingSeats, onBookingSeatsChange, onReserveNow, reserveLoading }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="xl"
    PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
  >
    <DialogTitle sx={{ p: 0, borderBottom: "1px solid #e6ebf2", background: "#fff" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", lineHeight: 1.1 }}>
              Détails du voyage
            </Typography>
            
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              label={ride?.statusLabel || "--"}
              sx={{
                height: 30,
                borderRadius: "999px",
                background: ride?.statusTone === "is-past" ? "#f2f5f9" : "#ecf8ee",
                color: ride?.statusTone === "is-past" ? "#6c7787" : "#0b7a45",
                fontWeight: 800,
                "& .MuiChip-label": { px: 1.2 },
              }}
            />
            <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}>
              Fermer
            </Button>
          </Stack>
        </Stack>
      </Box>
    </DialogTitle>

    <DialogContent sx={{ p: 0, background: "#f3f6fb" }}>
      {loading ? (
        <Box sx={{ minHeight: 420, display: "grid", placeItems: "center", gap: 2, p: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
            Chargement des détails du trajet...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : ride ? (
        <Box sx={{ p: { xs: 2, md: 2.5 } }} className="ride-details-main">
          <div className="ride-details-grid">
            <div className="ride-details-left">
              <article className="ride-itinerary-card">
                <div className="ride-summary-timeline">
                  <div className="ride-summary-row">
                    <div className="ride-summary-time">
                      <strong>{ride.departureTime || "--:--"}</strong>
                      <span>{ride.tripDuration || "--"}</span>
                    </div>
                    <div className="ride-summary-track" aria-hidden="true">
                      <span className="ride-summary-dot" />
                      <span className="ride-summary-line" />
                      <span className="ride-summary-dot is-end" />
                    </div>
                    <div className="ride-summary-stop">
                      <strong>{ride.originLabel}</strong>
                      <span>Point de départ</span>
                    </div>
                  </div>

                  <div className="ride-summary-stop-label">
                    <ClockMiniIcon />
                    <span>
                      {ride.tripDuration || "--"} · {ride.stopCount ?? 0} arrêt{(ride.stopCount ?? 0) > 1 ? "s" : ""}
                    </span>
                  </div>

                  {ride.waypoints.length ? (
                    <Box sx={{ pl: "80px", display: "grid", gap: 1.1, mt: 0.5 }}>
                      {ride.waypoints.map((waypoint) => (
                        <Box
                          key={waypoint.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "12px minmax(0, 1fr)",
                            gap: 1,
                            alignItems: "start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "999px",
                              border: "2px solid #1b273f",
                              background: "#fff",
                              mt: 0.6,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 800 }}>
                              {waypoint.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                              Séquence {waypoint.sequence} · Offset {waypoint.offsetLabel}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : null}

                  <div className="ride-summary-row is-arrival">
                    <div className="ride-summary-time">
                      <strong>{ride.arrivalTime || "--:--"}</strong>
                      <span>Arrivée estimée</span>
                    </div>
                    <div className="ride-summary-track" aria-hidden="true">
                      <span className="ride-summary-dot" />
                      <span className="ride-summary-line" />
                      <span className="ride-summary-dot is-end" />
                    </div>
                    <div className="ride-summary-stop">
                      <strong>{ride.destinationLabel}</strong>
                      <span>Point d'arrivée</span>
                    </div>
                  </div>
                </div>

              
              </article>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: "1px solid #e6ebf2",
                  background: "#fff",
                  p: 2,
                  boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      flex: "1 1 190px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.8,
                      alignContent: "start",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={ride.modeLabel || "Covoiturage"}
                        size="small"
                        sx={{
                          width: "fit-content",
                          height: 24,
                          borderRadius: "999px",
                          background: "#edf8ec",
                          color: "#0b7a45",
                          fontWeight: 900,
                          "& .MuiChip-label": { px: 1.1 },
                        }}
                      />
                      <Chip
                        label={ride.comfortClass || "standard"}
                        size="small"
                        sx={{
                          width: "fit-content",
                          height: 24,
                          borderRadius: "999px",
                          background: "#edf8ec",
                          color: "grey",
                          fontWeight: 900,
                          "& .MuiChip-label": { px: 1.1 },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ flex: "1 1 190px", display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DirectionsCarOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.2 }}>
                        {ride.vehicleNo || "--"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                        {ride.vehicleName || "--"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: "1 1 190px", display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <EventSeatOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.2 }}>
                        Sièges disponibles
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                        {ride.availableSeats}/{ride.totalSeats || "--"} sièges
                      </Typography>
                    </Box>
                  </Box>

                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: "1px solid #e6ebf2",
                    background: "#fff",
                    p: 1.25,
                    boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
                    marginTop : "5px",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900, mb: 1.5 }}>
                    Conducteur
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                   <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PersonOutlineOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.2 }}>
                        {ride.driverName || "--"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                        {ride.phone || "--"}
                      </Typography>
                    </Box>
                  </Box>
                  </Stack>
                </Paper>
              </Paper>

            </div>
            

            <aside className="ride-summary-panel">
            

              <div className="ride-booking-card">
                <div className="ride-availability">
                  <ClockMiniIcon />
                  <span>{ride.availableSeats} place{ride.availableSeats > 1 ? "s" : ""} disponible{ride.availableSeats > 1 ? "s" : ""}</span>
                </div>

                <Box sx={{ mt: 1.5, display: "grid", gap: 0.8 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800 }}>
                    Sièges à réserver
                  </Typography>
                  <Select
                    size="small"
                    value={bookingSeats}
                    onChange={(event) => onBookingSeatsChange(Number(event.target.value))}
                    disabled={!ride.availableSeats}
                    sx={{
                      borderRadius: 2.5,
                      background: "#fff",
                      "& .MuiSelect-select": {
                        py: 1,
                        fontWeight: 800,
                        color: "#10233f",
                      },
                    }}
                  >
                    {Array.from({ length: Math.max(1, Number(ride.availableSeats || 1)) }, (_, index) => index + 1).map((seatCount) => (
                      <MenuItem key={seatCount} value={seatCount}>
                        {seatCount}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <div className="ride-price-row">
                  <div className="ride-passenger-count">
                  </div>
                  <div className="ride-price-block">
                    <strong>{formatTripMoney(ride.pricePerSeat, ride.currency)}</strong>
                    <span>per seat</span>
                  </div>
                </div>

                <button type="button" className="ride-book-now-btn" onClick={onReserveNow} disabled={reserveLoading || !ride.availableSeats}>
                  {reserveLoading ? "Patientez..." : "Réserver maintenant"}
                </button>
              </div>
            </aside>
          </div>
        </Box>
      ) : null}
    </DialogContent>
  </Dialog>
);

export default function TrajetClient() {
  const dispatch = useDispatch();
  const timezone = useSelector((state) => state?.timezone?.timezone);
  const lewooPayToken = useSelector(
    (state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || ""
  );
  const [activePanel, setActivePanel] = useState(null);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [originQuery, setOriginQuery] = useState("");
  const [destinationQuery, setDestinationQuery] = useState("");
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [originLoading, setOriginLoading] = useState(false);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [selectedComfortClass, setSelectedComfortClass] = useState("all");
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [passengers, setPassengers] = useState([
    { id: 1, category: passengerCategoryOptions[1], age: "10", expanded: false },
  ]);
  const [editingPassengerId, setEditingPassengerId] = useState(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [temporaryCategory, setTemporaryCategory] = useState(passengerCategoryOptions[0]);
  const [temporaryAge, setTemporaryAge] = useState("10");
  const [appliedSearch, setAppliedSearch] = useState({
    origin: "",
    destination: "",
    travelDate: "",
    passengers: 1,
  });
  const [originPlaceId, setOriginPlaceId] = useState("");
  const [destinationPlaceId, setDestinationPlaceId] = useState("");
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [pastTrips, setPastTrips] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const passengerId = useSelector((state) => state?.AuthReducer?.userid || localStorage.getItem("userid") || "");
  const isAuthenticated = useSelector((state) => state?.AuthReducer?.isAuthenticated || false);
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});

  const [searchAttempted, setSearchAttempted] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [rideDetailsOpen, setRideDetailsOpen] = useState(false);
  const [rideDetailsLoading, setRideDetailsLoading] = useState(false);
  const [rideDetailsError, setRideDetailsError] = useState("");
  const [selectedRideDetails, setSelectedRideDetails] = useState(null);
  const [bookingSeats, setBookingSeats] = useState(1);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
  const [reserveModalOpen, setReserveModalOpen] = useState(false);
  const [authForm, setAuthForm] = useState({ email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [loadedAt, setLoadedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const searchRef = useRef(null);
  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const passengerEditorRef = useRef(null);

  const loadPassengerRides = useCallback(async () => {
    if (!passengerId) {
      setUpcomingTrips([]);
      setPastTrips([]);
      setLoadError("Impossible de charger les trajets sans identifiant utilisateur.");
      return;
    }

    setIsLoading(true);
    setLoadError("");
    dispatch(showLoader("Chargement des trajets..."));

    try {
      const res = await passengerApi.getPassengerRides({ id: passengerId });

      if (res?.data?.status === true) {
        const aVenir = Array.isArray(res?.data?.data?.a_venir) ? res.data.data.a_venir : [];
        const passe = Array.isArray(res?.data?.data?.passe) ? res.data.data.passe : [];
        setUpcomingTrips(aVenir);
        setPastTrips(passe);
        setLoadedAt(new Date());
      } else {
        setUpcomingTrips([]);
        setPastTrips([]);
        setLoadError(res?.data?.message || "Aucun trajet trouv?.");
      }
    } catch (error) {
      setUpcomingTrips([]);
      setPastTrips([]);
      const message = error?.response?.data?.message || "Erreur lors du chargement des trajets.";
      setLoadError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setIsLoading(false);
      dispatch(hideLoader());
    }
  }, [dispatch, passengerId]);

  const loadRides = useCallback(async ({ origin_place_id, destination_place_id, departure_at, comfort_class }) => {
    if (!origin_place_id || !destination_place_id) {
      setSearchResults([]);
      setSearchMessage("Renseignez départ et destination pour lancer la recherche.");
      setSearchAttempted(true);
      setSearchLoading(false);
      return;
    }

    setSearchAttempted(true);
    setSearchMessage("");
    setSearchResults([]);
    setSearchLoading(true);
    dispatch(showLoader("Recherche des trajets..."));

    try {
      const normalizedComfortClass = comfort_class && comfort_class !== "all" ? comfort_class : "";
      const res = await RideApi.getRideList({
        origin_place_id,
        destination_place_id,
        departure_at: departure_at || "",
        // comfort_class: normalizedComfortClass,
        page: 1,
        limit: "unlimited",
      });

      const payload = res?.data;
      const rawRows = [
        payload,
        payload?.rides,
        payload?.results,
        payload?.data?.rides,
        payload?.data?.results,
        payload?.data?.data?.rides,
        payload?.data?.data?.results,
        payload?.data?.data,
        payload?.data,
      ].find(Array.isArray) || [];

      setSearchResults(rawRows.map((trip) => normalizeTripDetail(trip?.ride || trip?.rides || trip, timezone)));
      setSearchMessage(rawRows.length ? "" : "Aucun trajet trouvé pour ces critères.");
    } catch (error) {
      const message = error?.response?.data?.message || "Erreur lors de la recherche des trajets.";
      setSearchMessage(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setSearchLoading(false);
      dispatch(hideLoader());
    }
  }, [dispatch]);

  const openRideDetails = useCallback(async (trip) => {
    const rideId = trip?.id || trip?.rideId || trip?.raw?.id || trip?.raw?.ride_id;
    if (!rideId) {
      dispatch(
        showSnackbar({
          message: "Impossible d'ouvrir les d?tails de ce trajet.",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    setRideDetailsOpen(true);
    setRideDetailsLoading(true);
    setRideDetailsError("");
    setSelectedRideDetails(normalizeTripDetail(trip, timezone));

    try {
      const response = await RideApi.getRideById(rideId);
      const payload = response?.data?.data || response?.data || {};
      setSelectedRideDetails(normalizeTripDetail(payload, timezone));
    } catch (error) {
      const message = error?.response?.data?.message || "Impossible de charger les d?tails du trajet.";
      setRideDetailsError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setRideDetailsLoading(false);
    }
  }, [dispatch, timezone]);

  const closeRideDetails = useCallback(() => {
    setRideDetailsOpen(false);
    setRideDetailsLoading(false);
    setRideDetailsError("");
    setSelectedRideDetails(null);
    setBookingSeats(1);
    setPaymentModalOpen(false);
    setSelectedPaymentMode("");
  }, []);

  useEffect(() => {
    const availableSeats = Number(selectedRideDetails?.availableSeats ?? 0);
    setBookingSeats(availableSeats > 0 ? availableSeats : 1);
  }, [selectedRideDetails?.id, selectedRideDetails?.availableSeats]);

  const resolveWalletPaymentMethodId = useCallback(async () => {
    const token = String(lewooPayToken || "").trim();

    if (!token) {
      return "";
    }

    const response = await WalletApi.getClientWallets(token);
    const wallets = Array.isArray(response?.data?.data) ? response.data.data : [];

    if (!wallets.length) {
      return "";
    }

    const primaryWallet = wallets.find((wallet) => wallet?.is_primary) || wallets[0] || null;
    return primaryWallet?.id || primaryWallet?._id || "";
  }, [lewooPayToken]);

  const buildBookingPayload = useCallback((paymentMode) => {
    const ride = selectedRideDetails?.raw || selectedRideDetails || {};
    const passengerCount = Math.max(1, Number(bookingSeats || 1));

    return {
      ride_id: ride?.ride_id || ride?.id || selectedRideDetails?.id || "",
      seats_booked: passengerCount,
      payment_method: String(paymentMode || "").toUpperCase(),
      payment_method_id: paymentMode === "wallet" ? "" : paymentMode || "",
    };
  }, [bookingSeats, selectedRideDetails]);

  const reserveRide = useCallback(async (paymentMode = selectedPaymentMode) => {
    const payload = buildBookingPayload(paymentMode);
    if (!payload.ride_id) {
      dispatch(
        showSnackbar({
          message: "Impossible de r?server ce trajet pour le moment.",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    if (paymentMode === "wallet") {
      try {
        payload.payment_method_id = await resolveWalletPaymentMethodId();
      } catch (error) {
        dispatch(
          showSnackbar({
            message: error?.response?.data?.message || "Impossible de charger le wallet principal.",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        return;
      }

      if (!payload.payment_method_id) {
        dispatch(
          showSnackbar({
            message: "Aucun wallet principal trouvé.",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        return;
      }
    }

    dispatch(showLoader("Loading please wait..."));
    setReserveLoading(true);
    try {
      const response = await BookingApi.createBookingV2(payload);
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: response?.data?.message || "R?servation effectu?e avec succ?s",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        })
      );
      setReserveModalOpen(false);
      console.log(response)
      const bookingData = response?.data?.booking || response?.data?.data || response?.data || null;
      console.log('')
      const confirmationPayload = {
        bookingId: bookingData?.id || bookingData?.booking_id || bookingData?.bookingId || "",
        booking: bookingData,
        ride: selectedRideDetails?.raw || selectedRideDetails || null,
        passengerCount: Math.max(1, Number(bookingSeats || 1)),
        passengerEmail:
          bookingData?.user?.email ||
          bookingData?.email ||
        authUser?.email ||
        localStorage.getItem("email") ||
        "",
      };
      sessionStorage.setItem("bookingConfirmationPayload", JSON.stringify(confirmationPayload));
      window.location.assign(`/booking-confirmation?bookingId=${confirmationPayload.bookingId || ""}`);
    } catch (error) {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || "Erreur lors de la r?servation",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setReserveLoading(false);
    }
  }, [
    authUser?.email,
    bookingSeats,
    buildBookingPayload,
    dispatch,
    resolveWalletPaymentMethodId,
    selectedPaymentMode,
    selectedRideDetails,
  ]);

  const handleReserveNow = useCallback(() => {
    setSelectedPaymentMode("");
    setPaymentModalOpen(true);
  }, []);

  const handlePaymentModeSelect = useCallback((mode) => {
    setSelectedPaymentMode(mode);
  }, []);

  const handlePaymentConfirm = useCallback(() => {
    if (!selectedPaymentMode) {
      return;
    }

    setPaymentModalOpen(false);

    if (!isAuthenticated) {
      setAuthError("");
      setReserveModalOpen(true);
      return;
    }

    void reserveRide(selectedPaymentMode);
  }, [isAuthenticated, reserveRide, selectedPaymentMode]);

  const handleAuthSubmit = useCallback(async (event) => {
    event.preventDefault();
    setAuthError("");
    setReserveLoading(true);

    try {
      const res = await loginApi.login({ email: authForm.email, password: authForm.password });
      const authData = getAuthPayload(res?.data);
      const token = persistAuthSession(authData);
      if (!token) {
        throw new Error("Connexion impossible");
      }

      dispatch(setAuthentication(authData));
      setReserveModalOpen(false);
      await reserveRide(selectedPaymentMode);
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Email ou mot de passe incorrect";
      setAuthError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setReserveLoading(false);
    }
  }, [authForm.email, authForm.password, dispatch, reserveRide, selectedPaymentMode]);

  useEffect(() => {
    loadPassengerRides();
  }, [loadPassengerRides]);

  useEffect(() => {
    if (!activePanel && !editingPassengerId) return undefined;

    const handleOutsideClick = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setActivePanel(null);
      }

      if (editingPassengerId && !passengerEditorRef.current?.contains(event.target)) {
        setEditingPassengerId(null);
        setCategoryMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActivePanel(null);
        setEditingPassengerId(null);
        setCategoryMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activePanel, editingPassengerId]);

  useEffect(() => {
    const currentQuery = activePanel === "origin"
      ? originQuery
      : activePanel === "destination"
        ? destinationQuery
        : "";

    if (!activePanel || currentQuery.trim().length < 2) {
      if (activePanel === "origin") setOriginSuggestions([]);
      if (activePanel === "origin") setOriginLoading(false);
      if (activePanel === "destination") setDestinationSuggestions([]);
      if (activePanel === "destination") setDestinationLoading(false);
      return undefined;
    }

    const panel = activePanel;
    const query = currentQuery.trim();
    let isActive = true;

    const timer = window.setTimeout(() => {
      const loadPlaces = async () => {
        try {
          if (!isActive) return;
          if (panel === "origin") setOriginLoading(true);
          if (panel === "destination") setDestinationLoading(true);

          const response = await PlaceService.getPlaceList({ search: query });
          const results = response?.data?.data?.data || [];
          const normalized = results.map(formatPlaceResult);

          if (!isActive) return;
          if (panel === "origin") setOriginSuggestions(normalized);
          if (panel === "destination") setDestinationSuggestions(normalized);
        } catch (error) {
          if (!isActive) return;
          if (panel === "origin") setOriginSuggestions([]);
          if (panel === "destination") setDestinationSuggestions([]);
        } finally {
          if (!isActive) return;
          if (panel === "origin") setOriginLoading(false);
          if (panel === "destination") setDestinationLoading(false);
        }
      };

      loadPlaces();
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timer);
    };
  }, [activePanel, originQuery, destinationQuery]);

  const today = new Date();
  const isToday = selectedDate.toDateString() === today.toDateString();
  const dateLabel = isToday
    ? `Aujourd'hui, ${formatFrenchDate(selectedDate, "short")}`
    : formatFrenchDate(selectedDate, "short");
  const monthCells = buildMonthGrid(calendarMonth);

  const openPanel = (panel) => {
    setActivePanel((current) => {
      const nextPanel = current === panel ? null : panel;

      if (nextPanel === "origin") {
        setOriginQuery((currentQuery) => currentQuery || origin);
        window.setTimeout(() => originInputRef.current?.focus(), 0);
      }

      if (nextPanel === "destination") {
        setDestinationQuery((currentQuery) => currentQuery || destination);
        window.setTimeout(() => destinationInputRef.current?.focus(), 0);
      }

      return nextPanel;
    });
  };

  const goToPreviousMonth = () => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
  };

  const updatePassenger = (id, patch) => {
    setPassengers((current) => current.map((passenger) => (
      passenger.id === id ? { ...passenger, ...patch } : passenger
    )));
  };

  const savePassengerEditor = () => {
    if (!editingPassengerId) return;
    updatePassenger(editingPassengerId, { category: temporaryCategory, age: temporaryAge });
    setEditingPassengerId(null);
    setCategoryMenuOpen(false);
  };

  const deletePassenger = () => {
    if (!editingPassengerId) return;
    setPassengers((current) => current.filter((passenger) => passenger.id !== editingPassengerId));
    setEditingPassengerId(null);
    setCategoryMenuOpen(false);
  };

  const selectPlace = (panel, place) => {
    if (panel === "origin") {
      setOrigin(place.title);
      setOriginQuery(place.title);
      setOriginPlaceId(place.id);
    }
    if (panel === "destination") {
      setDestination(place.title);
      setDestinationQuery(place.title);
      setDestinationPlaceId(place.id);
    }
    setActivePanel(null);
  };

  const appliedDate = formatDateForApi(selectedDate);
  const filteredTrips = useMemo(() => {
    const normalizeSearch = (value) => String(value || "").trim().toLowerCase();
    const originQueryValue = normalizeSearch(appliedSearch.origin);
    const destinationQueryValue = normalizeSearch(appliedSearch.destination);

    const matchesTrip = (trip) => {
      const originValue = normalizeSearch(
        trip?.originLabel ||
          trip?.origin_place?.name ||
          trip?.origin_place_name ||
          ""
      );
      const destinationValue = normalizeSearch(
        trip?.destinationLabel ||
          trip?.destination_place?.name ||
          trip?.destination_place_name ||
          ""
      );
      const departureValue = trip?.departureAt
        ? moment(trip.departureAt).tz(timezone || "Africa/Douala").format("YYYY-MM-DD")
        : "";

      const originOk = !originQueryValue || originValue.includes(originQueryValue);
      const destinationOk = !destinationQueryValue || destinationValue.includes(destinationQueryValue);
      const dateOk = !appliedSearch.travelDate || departureValue === appliedSearch.travelDate;
      return originOk && destinationOk && dateOk;
    };

    return {
      upcoming: upcomingTrips.filter(matchesTrip),
      past: pastTrips.filter(matchesTrip),
    };
  }, [
    appliedSearch.destination,
    appliedSearch.origin,
    appliedSearch.travelDate,
    pastTrips,
    timezone,
    upcomingTrips,
  ]);

  const recentSearches = useMemo(() => {
    const toLabel = (trip) => ({
      from:
        trip?.originLabel ||
        trip?.origin_place?.name ||
        trip?.origin_place_name ||
        "D?part",
      to:
        trip?.destinationLabel ||
        trip?.destination_place?.name ||
        trip?.destination_place_name ||
        "Arriv?e",
      date: trip?.departureAt
        ? moment(trip.departureAt).tz(timezone || "Africa/Douala").format("D MMM")
        : "Aujourd'hui",
    });

    const combined = [...filteredTrips.upcoming, ...filteredTrips.past].slice(0, 3).map(toLabel);
    return combined.length > 0 ? combined : recentFallback;
  }, [filteredTrips.past, filteredTrips.upcoming, timezone]);

  const handleSearch = (event) => {
    event?.preventDefault?.();
    setSearchAttempted(true);
    setAppliedSearch({
      origin: (originQuery || origin).trim(),
      destination: (destinationQuery || destination).trim(),
      travelDate: appliedDate,
      comfortClass: selectedComfortClass,
      passengers: passengers.length,
    });
    void loadRides({
      origin_place_id: originPlaceId || originQuery || origin,
      destination_place_id: destinationPlaceId || destinationQuery || destination,
      departure_at: appliedDate,
      comfort_class: selectedComfortClass,
    });
  };

  const editingPassenger = passengers.find((passenger) => passenger.id === editingPassengerId) || null;

  const lastUpdatedLabel = loadedAt
    ? moment(loadedAt).tz(timezone || "Africa/Douala").format("DD/MM/YYYY HH:mm")
    : "--";

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gap: 3,
          pb: 3,
          pt: { xs: 1, md: 0.5 },
        }}
      >
        <Box sx={{ display: "grid", gap: 1 }}>
          <Typography variant="h4" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.1 }}>
            Rechercher un trajet
          </Typography>
          <Typography variant="body1" sx={{ color: "#6a778a", fontWeight: 600 }}>
            Trouvez et réservez votre prochain voyage en quelques clics.
          </Typography>
        </Box>

        <section className="trajet-search bbc-search">
          <div className="bbc-search-card" ref={searchRef}>
            <div className="bbc-search-row">
              <div className={`bbc-field-wrap ${activePanel === "origin" ? "is-active" : ""}`}>
                <div
                  className="bbc-field bbc-field-button"
                  aria-expanded={activePanel === "origin"}
                  onClick={() => setActivePanel("origin")}
                >
                  <span className="bbc-search-icon-bubble">
                    <CirclePinIcon />
                  </span>
                  <div className="bbc-field-content">
                    {activePanel === "origin" ? (
                      <input
                        ref={originInputRef}
                        type="text"
                        value={originQuery}
                        onChange={(event) => {
                          setOriginQuery(event.target.value);
                          setActivePanel("origin");
                        }}
                        onFocus={() => setActivePanel("origin")}
                        placeholder="Ville ou gare de départ"
                        aria-label="Ville ou gare de départ"
                      />
                    ) : (
                      <>
                        <strong>{origin || "Depart"}</strong>
                        <small>{originLoading ? "Recherche..." : "Ville ou gare de départ"}</small>
                      </>
                    )}
                  </div>
                </div>
                {activePanel === "origin" ? (
                  <div className="bbc-popover bbc-location-popover" role="listbox" aria-label="Suggestions de départ">
                    {originLoading ? (
                      <div className="bbc-suggestion bbc-suggestion-loader" aria-live="polite">
                        <span className="bbc-suggestion-spinner" aria-hidden="true" />
                        <div>
                          <strong>Recherche en cours</strong>
                          <span>Nous cherchons les lieux disponibles</span>
                        </div>
                      </div>
                    ) : originSuggestions.length ? originSuggestions.map((item, index) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`bbc-suggestion ${index === 0 ? "is-selected" : ""}`}
                        onClick={() => {
                          selectPlace("origin", item);
                        }}
                      >
                        <div>
                          <strong>{item.title}</strong>
                          <span>{item.subtitle}</span>
                        </div>
                        <ArrowIcon />
                      </button>
                    )) : (
                      <div className="bbc-suggestion bbc-suggestion-empty">
                        <div>
                          <strong>Aucun résultat</strong>
                          <span>Essayez une autre recherche</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className={`bbc-field-wrap ${activePanel === "destination" ? "is-active" : ""}`}>
                <div
                  className="bbc-field bbc-field-button"
                  aria-expanded={activePanel === "destination"}
                  onClick={() => setActivePanel("destination")}
                >
                  <span className="bbc-search-icon-bubble">
                    <CirclePinIcon />
                  </span>
                  <div className="bbc-field-content">
                    {activePanel === "destination" ? (
                      <input
                        ref={destinationInputRef}
                        type="text"
                        value={destinationQuery}
                        onChange={(event) => {
                          setDestinationQuery(event.target.value);
                          setActivePanel("destination");
                        }}
                        onFocus={() => setActivePanel("destination")}
                        placeholder="Ville ou gare d'arriv?e"
                        aria-label="Ville ou gare d'arriv?e"
                      />
                    ) : (
                      <>
                        <strong>{destination || "Destination"}</strong>
                        <small>{destinationLoading ? "Recherche..." : "Ville ou gare d'arriv?e"}</small>
                      </>
                    )}
                  </div>
                </div>
                {activePanel === "destination" ? (
                  <div className="bbc-popover bbc-location-popover" role="listbox" aria-label="Suggestions de destination">
                    {destinationLoading ? (
                      <div className="bbc-suggestion bbc-suggestion-loader" aria-live="polite">
                        <span className="bbc-suggestion-spinner" aria-hidden="true" />
                        <div>
                          <strong>Recherche en cours</strong>
                          <span>Nous cherchons les lieux disponibles</span>
                        </div>
                      </div>
                    ) : destinationSuggestions.length ? destinationSuggestions.map((item, index) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`bbc-suggestion ${index === 0 ? "is-selected" : ""}`}
                        onClick={() => {
                          selectPlace("destination", item);
                        }}
                      >
                        <div>
                          <strong>{item.title}</strong>
                          <span>{item.subtitle}</span>
                        </div>
                        <ArrowIcon />
                      </button>
                    )) : (
                      <div className="bbc-suggestion bbc-suggestion-empty">
                        <div>
                          <strong>Aucun r?sultat</strong>
                          <span>Essayez une autre recherche</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              <div className={`bbc-field-wrap ${activePanel === "date" ? "is-active" : ""}`}>
                <button
                  type="button"
                  className="bbc-field bbc-field-button"
                  onClick={() => openPanel("date")}
                  aria-expanded={activePanel === "date"}
                >
                  <span className="bbc-search-icon-bubble">
                    <CalendarIcon />
                  </span>
                  <div>
                    <strong>{isToday ? "Aujourd'hui" : formatFrenchDate(selectedDate, "short")}</strong>
                    <small>{dateLabel}</small>
                  </div>
                </button>
                {activePanel === "date" ? (
                  <div className="bbc-popover bbc-calendar-popover" role="dialog" aria-label="Choisir une date">
                    <div className="bbc-calendar-head">
                      <button type="button" className="bbc-calendar-nav is-previous" onClick={goToPreviousMonth} aria-label="Mois précédent">
                        <ArrowIcon />
                      </button>
                      <strong>{formatFrenchDate(calendarMonth, "long").replace(/\b\w/, (letter) => letter.toUpperCase())}</strong>
                      <button type="button" className="bbc-calendar-nav" onClick={goToNextMonth} aria-label="Mois suivant">
                        <ArrowIcon />
                      </button>
                    </div>
                    <div className="bbc-calendar-weekdays" aria-hidden="true">
                      <span>Lun</span>
                      <span>Mar</span>
                      <span>Mer</span>
                      <span>Jeu</span>
                      <span>Ven</span>
                      <span>Sam</span>
                      <span>Dim</span>
                    </div>
                    <div className="bbc-calendar-grid">
                      {monthCells.map((day, index) => {
                        const isSelected = day && day.toDateString() === selectedDate.toDateString();
                        const isCurrent = day && day.toDateString() === today.toDateString();
                        return day ? (
                          <button
                            key={day.toISOString()}
                            type="button"
                            className={`bbc-calendar-day ${isSelected ? "is-selected" : ""} ${isCurrent ? "is-today" : ""}`}
                            onClick={() => {
                              setSelectedDate(day);
                              setActivePanel(null);
                            }}
                          >
                            {day.getDate()}
                          </button>
                        ) : (
                          <span key={`empty-${index}`} className="bbc-calendar-empty" />
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* <div className="bbc-field-wrap">
                <div className="bbc-field" style={{ paddingTop: 10, paddingBottom: 10 }}>
                  <span className="bbc-search-icon-bubble">
                    <DirectionsCarOutlined />
                  </span>
                  <div className="bbc-field-content" style={{ minWidth: 0, flex: 1 }}>
                    <Select
                      fullWidth
                      value={selectedComfortClass}
                      onChange={(event) => setSelectedComfortClass(event.target.value)}
                      displayEmpty
                      variant="standard"
                      disableUnderline
                      sx={{
                        width: "100%",
                        color: "#061f4a",
                        fontSize: 12,
                        fontWeight: 800,
                        "& .MuiSelect-select": {
                          py: 0,
                          px: 0,
                          minHeight: "auto",
                          display: "flex",
                          alignItems: "center",
                        },
                        "& .MuiSelect-icon": {
                          color: "#6d7b92",
                          right: 0,
                        },
                      }}
                    >
                      {comfortClassOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </div>
              </div> */}

              <button
                type="button"
                className="bbc-search-btn"
                onClick={handleSearch}
                disabled={searchLoading}
              >
                <span>{searchLoading ? "Recherche..." : "Rechercher"}</span>
                <ArrowIcon />
              </button>
            </div>
          </div>
        </section>

        {editingPassenger ? (
          <div className="bbc-passenger-modal-backdrop">
            <div className="bbc-passenger-modal" ref={passengerEditorRef} role="dialog" aria-modal="true" aria-label="Informations passager">
              <button
                type="button"
                className="bbc-passenger-modal-close"
                aria-label="Fermer"
                onClick={() => {
                  setEditingPassengerId(null);
                  setCategoryMenuOpen(false);
                }}
              >
                <span />
                <span />
              </button>

              <h3>Informations passager</h3>

              <div className="bbc-passenger-modal-field">
                <label>Catégorie de passager</label>
                <button
                  type="button"
                  className="bbc-passenger-modal-select"
                  onClick={() => setCategoryMenuOpen((current) => !current)}
                >
                  <span>{temporaryCategory}</span>
                  <ChevronDownIcon />
                </button>
                {categoryMenuOpen ? (
                  <div className="bbc-passenger-modal-menu" role="listbox" aria-label="Catégorie de passager">
                    {passengerCategoryOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`bbc-passenger-modal-option ${temporaryCategory === option ? "is-selected" : ""}`}
                        onClick={() => {
                          setTemporaryCategory(option);
                          updatePassenger(editingPassenger.id, { category: option });
                          setCategoryMenuOpen(false);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="bbc-passenger-modal-field">
                <label>Âge le jour du trajet</label>
                <Select
                  fullWidth
                  value={temporaryAge}
                  onChange={(event) => {
                    const nextAge = String(event.target.value);
                    setTemporaryAge(nextAge);
                    updatePassenger(editingPassenger.id, { age: nextAge });
                  }}
                  displayEmpty
                  variant="outlined"
                  IconComponent={ChevronDownIcon}
                  sx={{
                    mt: 1,
                    borderRadius: 3,
                    background: "#f8fbff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b8c9e6",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7ea0d6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4f7fd1",
                    },
                    "& .MuiSelect-select": {
                      py: 1.4,
                      px: 1.8,
                      fontWeight: 700,
                      color: "#10233f",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#52627d",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 280,
                        borderRadius: 3,
                        mt: 1,
                      },
                    },
                  }}
                >
                  {passengerAgeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div className="bbc-passenger-modal-actions">
                <button type="button" className="bbc-passenger-modal-delete" onClick={deletePassenger}>
                  Supprimer le passager
                </button>
                <button type="button" className="bbc-passenger-modal-save" onClick={savePassengerEditor}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900 }}>
              Trajets
            </Typography>
            <Typography variant="body2" sx={{ color: "#6a778a", fontWeight: 700 }}>
              {searchAttempted ? searchResults.length : 0} résultat{(searchAttempted ? searchResults.length : 0) > 1 ? "s" : ""}
            </Typography>
          </Stack>

          {searchLoading ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 3,
                border: "1px solid rgba(15, 39, 72, 0.08)",
                display: "grid",
                placeItems: "center",
                gap: 2,
                background: "#fff",
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
                Recherche en cours...
              </Typography>
            </Paper>
          ) : searchAttempted ? (
            searchResults.length ? (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {searchResults.map((trip, index) => (
                  <TripRowCard
                    key={trip?.id || trip?.rideId || `${index}`}
                    trip={trip}
                    timezone={timezone}
                    onOpenDetails={openRideDetails}
                  />
                ))}
              </Box>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: 3,
                  border: "1px solid rgba(15, 39, 72, 0.08)",
                  background: "#fff",
                }}
              >
                <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900 }}>
                  Aucun trajet trouvé
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, mt: 0.75 }}>
                  {searchMessage || "Aucun trajet ne correspond à ces critères."}
                </Typography>
              </Paper>
            )
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 3,
                border: "1px solid rgba(15, 39, 72, 0.08)",
                background: "#fff",
              }}
            >
              <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900 }}>
                Lancez une recherche
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, mt: 0.75 }}>
                Les résultats s’afficheront ici après clic sur Rechercher.
              </Typography>
            </Paper>
          )}
        </Box>

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900 }}>
              Suggestions populaires
            </Typography>
            <Typography variant="body2" sx={{ color: "#6a778a", fontWeight: 600, mt: 0.8 }}>
              Des trajets fréquemment recherchés par nos voyageurs
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {popularTrips.map((item) => (
              <PopularCard key={`${item.from}-${item.to}`} item={item} />
            ))}
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #b8dbc3",
            background: "linear-gradient(180deg, #f2fbf5 0%, #eaf8ed 100%)",
            boxShadow: "0 12px 26px rgba(8, 26, 52, 0.04)",
            px: { xs: 2, md: 2.5 },
            py: { xs: 2, md: 2.25 }, 
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "999px",
                  display: "grid",
                  placeItems: "center",
                  background: "rgba(13, 122, 69, 0.1)",
                  color: "#0d7a45",
                  flexShrink: 0,
                }} 
              >
                <ShieldOutlined />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: "#0d6f3f", fontWeight: 900 }}>
                  Voyagez en toute sécurité
                </Typography>
                <Typography variant="body2" sx={{ color: "#5e6d7f", fontWeight: 600, mt: 0.6 }}>
                  Tous nos trajets sont assurés et nos partenaires sont vérifiés pour votre sécurité.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              endIcon={<ArrowForwardRounded />}
              sx={{
                minWidth: { xs: "100%", md: 168 },
                minHeight: 44,
                borderRadius: 3,
                borderColor: "#b8dbc3",
                color: "#0d7a45",
                background: "#fff",
                textTransform: "none",
                fontWeight: 900,
                "&:hover": {
                  borderColor: "#96cfaa",
                  background: "#fbfefb",
                },
              }}
            >
              En savoir plus
            </Button>
          </Stack>
        </Paper>

        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        {isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: 4,
              border: "1px solid rgba(15, 39, 72, 0.08)",
              display: "grid",
              placeItems: "center",
              gap: 2,
              background: "#fff",
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 700 }}>
              Chargement des trajets...
            </Typography>
          </Paper>
        ) : null}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#6a778a", fontSize: 12, fontWeight: 700 }}>
          <RefreshRounded fontSize="small" />
          <span>Dernière mise à jour: {lastUpdatedLabel}</span>
        </Box>

        <RideDetailsModal
          open={rideDetailsOpen}
          ride={selectedRideDetails}
          loading={rideDetailsLoading}
          error={rideDetailsError}
          onClose={closeRideDetails}
          passengerCount={appliedSearch.passengers}
          bookingSeats={bookingSeats}
          onBookingSeatsChange={(value) => {
            const maxSeats = Number(selectedRideDetails?.availableSeats ?? 1);
            const normalized = Math.max(1, Math.min(Number(value) || 1, maxSeats || 1));
            setBookingSeats(normalized);
          }}
          onReserveNow={handleReserveNow}
          reserveLoading={reserveLoading}
        />

        <PaymentModeModal
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedPaymentMode("");
          }}
          onSelectMode={handlePaymentModeSelect}
          onConfirm={handlePaymentConfirm}
          selectedMode={selectedPaymentMode}
          loading={reserveLoading}
        />

        <ReservationLoginModal
          open={reserveModalOpen}
          onClose={() => setReserveModalOpen(false)}
          form={authForm}
          setForm={setAuthForm}
          onSubmit={handleAuthSubmit}
          loading={reserveLoading}
          error={authError}
        />

      </Box>
    </>
  );
}

function formatDateForApi(date) {
  if (!date) return "";
  if (typeof date === "string") return date.slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
 
